import { useCallback, useEffect, useState } from "react";
import { ScrollText, Search, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const ACTION_CLS = {
  login: "bg-slate-500/10 text-slate-500 border-slate-400/30",
  form_submitted: "bg-teal-500/10 text-teal-600 border-teal-500/30",
  lead_updated: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  lead_note_added: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  user_created: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  user_updated: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  settings_updated: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  crm_settings_updated: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  course_completed: "bg-violet-500/10 text-violet-600 border-violet-500/30",
  lesson_completed: "bg-violet-500/10 text-violet-600 border-violet-500/30",
  quiz_passed: "bg-violet-500/10 text-violet-600 border-violet-500/30",
  document_created: "bg-sky-500/10 text-sky-600 border-sky-500/30",
  document_generated: "bg-sky-500/10 text-sky-600 border-sky-500/30",
  document_pdf_exported: "bg-sky-500/10 text-sky-600 border-sky-500/30",
  document_acknowledged: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  template_published: "bg-sky-500/10 text-sky-600 border-sky-500/30",
  content_updated: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  content_published: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  form_definition_updated: "bg-amber-500/10 text-amber-600 border-amber-500/30",
};

const ALL = "__all__";

export default function AdminAudit() {
  const { t } = useLang();
  const [logs, setLogs] = useState(null);
  const [meta, setMeta] = useState({ actions: [], entity_types: [] });
  const [action, setAction] = useState(ALL);
  const [entity, setEntity] = useState(ALL);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/admin/audit-meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (action !== ALL) params.set("action", action);
    if (entity !== ALL) params.set("entity_type", entity);
    if (search.trim()) params.set("search", search.trim());
    params.set("limit", "200");
    api.get(`/admin/audit-logs?${params.toString()}`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, [action, entity, search]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const hasFilters = action !== ALL || entity !== ALL || search.trim();

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Audit-Logs</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("Unver\u00e4nderliches Protokoll aller kritischen Aktionen \u2013 Login, Leads, Academy, Dokumente, CMS", "Immutable log of all critical actions \u2013 login, leads, academy, documents, CMS")}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Benutzer, Aktion, ID\u2026", "User, action, ID\u2026")} className="pl-9 h-10 w-64 rounded-[10px]" data-testid="audit-search-input" />
        </div>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="h-10 w-56 rounded-[10px]" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={ALL}>{t("Alle Aktionen", "All actions")}</SelectItem>
            {meta.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger className="h-10 w-52 rounded-[10px]" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={ALL}>{t("Alle Objekttypen", "All entity types")}</SelectItem>
            {meta.entity_types.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" className="rounded-[10px] text-muted-foreground" onClick={() => { setAction(ALL); setEntity(ALL); setSearch(""); }} data-testid="audit-clear-filters">
            <X className="h-3.5 w-3.5 mr-1" /> {t("Filter zur\u00fccksetzen", "Clear filters")}
          </Button>
        )}
      </div>

      <Card className="rounded-[16px] overflow-hidden">
        {!logs ? (
          <CardContent className="p-6 space-y-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</CardContent>
        ) : logs.length === 0 ? (
          <CardContent className="py-16 text-center">
            <ScrollText className="h-8 w-8 text-muted-foreground/40 mx-auto" />
            <p className="mt-3 text-sm text-muted-foreground">{t("Keine Eintr\u00e4ge f\u00fcr diese Filter.", "No entries for these filters.")}</p>
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="audit-logs">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("Aktion", "Action")}</TableHead>
                  <TableHead>{t("Benutzer", "User")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("Objekt", "Entity")}</TableHead>
                  <TableHead>{t("Zeitpunkt", "Timestamp")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="cursor-pointer" onClick={() => setSelected(log)} data-testid={`audit-row-${log.id}`}>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full text-[10px] font-mono-num ${ACTION_CLS[log.action] || "bg-muted"}`}>{log.action}</Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{log.user_name || "System"}</p>
                      <p className="text-[10px] text-muted-foreground">{log.user_role || "\u2013"}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                      {log.entity_type}{log.entity_id ? ` \u00b7 ${String(log.entity_id).slice(0, 8)}` : ""}
                    </TableCell>
                    <TableCell className="font-mono-num text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-md">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.action}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p><span className="text-muted-foreground">{t("Benutzer", "User")}:</span> {selected.user_name || "System"} ({selected.user_role || "\u2013"})</p>
                <p><span className="text-muted-foreground">{t("Objekt", "Entity")}:</span> {selected.entity_type} {selected.entity_id}</p>
                <p><span className="text-muted-foreground">{t("Zeitpunkt", "Timestamp")}:</span> {new Date(selected.created_at).toLocaleString()}</p>
              </div>
              <pre className="rounded-[12px] bg-[#0B1220] text-white/85 p-4 text-xs overflow-x-auto" data-testid="audit-details-json">
                {JSON.stringify(selected.details, null, 2)}
              </pre>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
